import type { PRICING_PLANS } from "features/subscriptions/constants";

import RelativeLink from "common/components/RelativeLink";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import usePromotions from "common/datahooks/usePromotions";

import SubscriptionTypeCards from "./SubscriptionTypeCards";

import type { Dispatch, JSX, SetStateAction } from "react";

import LockedIcon from "assets/icons/locked.svg?react";
import AgencyCoverImage from "assets/images/plans/agency-cover.webp";
import AvatarCoverImage from "assets/images/plans/avatar-cover.webp";
import OutreachCoverImage from "assets/images/plans/outreach-cover.webp";

interface DesktopLayoutProps {
  title: string;
  subscriptionType: keyof typeof PRICING_PLANS;
  setSubscriptionType: Dispatch<SetStateAction<keyof typeof PRICING_PLANS>>;
  actionButton: JSX.Element;
}

export default function DesktopLayout({
  title,
  subscriptionType,
  setSubscriptionType,
  actionButton,
}: DesktopLayoutProps) {
  const { role } = useSelectedWorkspaceContext();
  const { appSumoPromotion, ltdPromotion } = usePromotions();

  let coverImage = OutreachCoverImage;

  if (subscriptionType === "rental") {
    coverImage = AvatarCoverImage;
  } else if (subscriptionType === "agency") {
    coverImage = AgencyCoverImage;
  }

  return (
    <section className="flex size-full items-center justify-center">
      <div className="w-225 h-135 rounded-4xl p-1.5 bg-whiteGray">
        <div className="flex h-full border border-black-200 rounded-3xl overflow-hidden shadow-md">
          <div className="flex flex-col gap-y-6 p-9 pb-6 w-105 bg-white">
            <div className="flex gap-x-4 items-center">
              <LockedIcon />

              <div className="flex flex-col gap-y-1">
                <h2 className="text-lg-bold text-left">{title}</h2>
                <p className="text-sm-medium text-black-500 text-left">
                  Choose a plan to proceed:
                </p>
              </div>
            </div>

            <SubscriptionTypeCards
              subscriptionType={subscriptionType}
              setSubscriptionType={setSubscriptionType}
            />

            <div className="mt-auto ml-auto flex gap-x-2.5">
              <RenderIf
                condition={
                  !!(appSumoPromotion || ltdPromotion) && role === "owner"
                }
              >
                <Button variant="secondary-black">
                  <RelativeLink to="settings/ltd">Lifetime seats</RelativeLink>
                </Button>
              </RenderIf>

              {actionButton}
            </div>
          </div>

          <div className="items-center justify-center flex grow bg-whiteGray">
            <img
              src={coverImage}
              className="w-full h-full object-cover"
              alt=""
            />
          </div>
        </div>
      </div>
    </section>
  );
}
